import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { getValueAtDescriptorPath } from "../elementEditing";
import { persistedCssColorError, registryFieldError } from "../inspector/inspectorModel";
import { VisualColorControl, VisualSliderControl } from "../inspector/VisualSettingControls";

function finiteLimit(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function rangeHelper(field) {
  const min = finiteLimit(field?.limits?.min);
  const max = finiteLimit(field?.limits?.max);
  if (min === undefined && max === undefined) return undefined;
  const unitMatch = String(field?.label || "").match(/\(([^)]+)\)\s*$/);
  const unit = unitMatch?.[1] ? ` ${unitMatch[1]}` : "";
  if (min !== undefined && max !== undefined) return `Permitido: ${min}–${max}${unit}`;
  if (min !== undefined) return `Mínimo: ${min}${unit}`;
  return `Máximo: ${max}${unit}`;
}

function fieldUnit(field) {
  const unitMatch = String(field?.label || "").match(/\(([^)]+)\)\s*$/);
  return unitMatch?.[1] || "";
}

function NumberField({ field, value, disabled, onCommit }) {
  const externalValue = value === undefined || value === null ? "" : String(value);
  const [draft, setDraft] = useState(externalValue);
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    setDraft(externalValue);
    setEdited(false);
  }, [externalValue]);

  const min = finiteLimit(field?.limits?.min);
  const max = finiteLimit(field?.limits?.max);
  const step = finiteLimit(field?.limits?.step);
  const commitDraft = () => {
    if (!edited) return;
    if (draft === "") {
      setEdited(false);
      onCommit?.(field.path, field?.defaultValue ?? "");
      return;
    }
    const parsed = Number(draft);
    if (!Number.isFinite(parsed)) {
      setDraft(externalValue);
      setEdited(false);
      return;
    }
    let normalized = parsed;
    if (min !== undefined) normalized = Math.max(min, normalized);
    if (max !== undefined) normalized = Math.min(max, normalized);
    setDraft(String(normalized));
    setEdited(false);
    onCommit?.(field.path, normalized);
  };

  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      label={field.label}
      value={draft}
      disabled={disabled}
      helperText={rangeHelper(field)}
      inputProps={{
        ...(min !== undefined ? { min } : {}),
        ...(max !== undefined ? { max } : {}),
        ...(step !== undefined ? { step } : {}),
      }}
      onChange={(event) => {
        setDraft(event.target.value);
        setEdited(true);
      }}
      onBlur={commitDraft}
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        event.target.blur();
      }}
    />
  );
}

function explicitFieldDefault(field, fallback = "") {
  return field?.defaultValue !== undefined ? field.defaultValue : fallback;
}

function mobileOwnerLabel(path) {
  const value = String(path || "");
  const rules = [
    ["navigation.mobile.shellStyle.", "Navbar móvil · fondo exterior"],
    ["navigation.mobile.barStyle.", "Navbar móvil · superficie de barra"],
    ["navigation.mobile.drawerStyle.", "Drawer · cuerpo"],
    ["navigation.mobile.drawerHeaderStyle.", "Drawer · encabezado"],
    ["navigation.mobile.openButtonStyle.", "Botón abrir menú"],
    ["navigation.mobile.closeButtonStyle.", "Botón cerrar menú"],
    ["navigation.links.home.mobileStyle.", "Inicio mobile"],
    ["navigation.links.catalog.mobileStyle.", "Catálogo mobile"],
    ["navigation.links.about.mobileStyle.", "Sobre nosotros mobile"],
    ["navigation.links.highlights.mobileStyle.", "Diferenciales mobile"],
    ["navigation.links.contact.mobileStyle.", "Contacto mobile"],
    ["navigation.loginStyles.mobile.", "Login · barra mobile"],
    ["navigation.loginStyles.drawer.", "Login · drawer"],
    ["navigation.accountStyles.mobile.", "Cuenta · barra mobile"],
    ["navigation.accountStyles.drawer.", "Cuenta · drawer"],
    ["navigation.cartStyles.mobile.", "Carrito mobile"],
    ["navigation.mobile.catalogPanelStyle.", "Panel de catálogo mobile"],
    ["navigation.mobile.catalogItemStyle.", "Opciones de catálogo mobile"],
    ["navigation.businessNameStyle.drawer.", "Nombre del negocio · drawer"],
    ["navigation.mobile.logo", "Logo mobile"],
  ];
  const match = rules.find(([prefix]) => value.startsWith(prefix));
  return match?.[1] || "Opciones mobile";
}

function MobileFieldControl({ field, form, disabled, onCommit }) {
  const value = getValueAtDescriptorPath(form, field.path, "");
  if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) {
    return (
      <FormControlLabel
        control={<Switch checked={value === true} disabled={disabled} onChange={(event) => onCommit?.(field.path, event.target.checked)} />}
        label={field.label}
        sx={{ m: 0, minHeight: 44 }}
      />
    );
  }
  if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{field.label}</InputLabel>
        <Select label={field.label} value={value ?? ""} onChange={(event) => onCommit?.(field.path, event.target.value)}>
          {(field.options || []).map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }
  if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
    return (
      <VisualColorControl
        label={field.label}
        value={value}
        fallback={explicitFieldDefault(field, "#19242F")}
        disabled={disabled}
        inheritedLabel="Predeterminado"
        helperText="Color explícito de este componente mobile; no hereda de desktop ni de una paleta global."
        allowExactCss
        validateExactValue={(candidate) => registryFieldError(field, candidate) || persistedCssColorError(candidate, field.maxLength || 100)}
        exactValueMaxLength={field.maxLength || 100}
        onChange={(next) => onCommit?.(field.path, next)}
        onReset={() => onCommit?.(field.path, explicitFieldDefault(field, "#19242F"))}
      />
    );
  }
  if (field.type === SEGMENT_FIELD_TYPES.NUMBER && field.control === "slider") {
    return (
      <VisualSliderControl
        label={field.label}
        value={value}
        min={field.limits?.min ?? 0}
        max={field.limits?.max ?? 100}
        step={field.limits?.step ?? 1}
        unit={fieldUnit(field)}
        disabled={disabled}
        helperText={field.helperText || rangeHelper(field)}
        nullable={false}
        fallbackValue={explicitFieldDefault(field, field.limits?.min ?? 0)}
        resetLabel={field.resetLabel || "Restablecer"}
        onChange={(next) => onCommit?.(field.path, next)}
        onReset={() => onCommit?.(field.path, explicitFieldDefault(field, field.limits?.min ?? 0))}
      />
    );
  }
  if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
    return <NumberField field={field} value={value} disabled={disabled} onCommit={onCommit} />;
  }
  return <TextField fullWidth size="small" type="text" label={field.label} value={value ?? ""} disabled={disabled} onChange={(event) => onCommit?.(field.path, event.target.value)} />;
}

export default function NavbarMobileElementEditor({ descriptor, form, disabled, onCommit }) {
  const fields = (descriptor?.fields || []).filter((field) => field?.editable !== false);
  const groups = useMemo(() => {
    const output = [];
    fields.forEach((field) => {
      const label = mobileOwnerLabel(field.path);
      let group = output.find((candidate) => candidate.label === label);
      if (!group) {
        group = { label, fields: [] };
        output.push(group);
      }
      group.fields.push(field);
    });
    return output;
  }, [fields]);

  return (
    <Stack spacing={1.1} sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
        Cada grupo edita un propietario mobile independiente. Restablecer vuelve al default explícito de ese componente.
      </Typography>
      {groups.map((group, index) => (
        <Accordion
          key={group.label}
          defaultExpanded={index === 0}
          disableGutters
          elevation={0}
          sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&::before": { display: "none" }, overflow: "hidden" }}
        >
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 48 }}>
            <Typography variant="body2" sx={{ fontWeight: 900 }}>{group.label}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={1.1}>
              {group.fields.map((field) => (
                <MobileFieldControl key={field.path} field={field} form={form} disabled={disabled} onCommit={onCommit} />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}
